export type FieldValidationState = 'valid' | 'invalid' | null;

export type ValidationField = {
    required?: boolean;
    validateWhenEmpty?: boolean;
    validationMessage?: string;
    requiredMessage?: string;
    validate?: (value: unknown, values: Record<string, unknown>) => boolean | string;
    type:
        | 'text'
        | 'number'
        | 'money'
        | 'date'
        | 'select'
        | 'yesno'
        | 'email'
        | 'postcode'
        | 'address';
};

const DEFAULT_REQUIRED_MESSAGE = 'Required';
const DEFAULT_VALIDATION_MESSAGE = 'Invalid value';

const normalizeMessage = (message: string | undefined): string | undefined => {
    if (typeof message !== 'string') return undefined;
    const trimmed = message.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

export const getFieldRequiredMessage = (
    field: Pick<ValidationField, 'requiredMessage'>,
    fallback = DEFAULT_REQUIRED_MESSAGE,
) => normalizeMessage(field.requiredMessage) || fallback;

const getFieldValidationMessage = (field: Pick<ValidationField, 'validationMessage'>) =>
    normalizeMessage(field.validationMessage) || DEFAULT_VALIDATION_MESSAGE;

export const isFieldValueEmpty = (
    field: Pick<ValidationField, 'type'>,
    value: unknown,
): boolean => {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (typeof value === 'number') return Number.isNaN(value);
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'boolean') return false;
    if (typeof value !== 'object') return false;

    if (field.type === 'address') {
        const address = value as Record<string, unknown>;
        const line1 = String(address.line1 ?? '').trim();
        const town = String(address.town ?? '').trim();
        const postcode = String(address.postcode ?? '').trim();
        return !(line1 && town && postcode);
    }

    return Object.keys(value as Record<string, unknown>).length === 0;
};

export const runFieldValidator = ({
    field,
    value,
    values,
}: {
    field: Pick<
        ValidationField,
        'required' | 'validate' | 'validateWhenEmpty' | 'validationMessage' | 'type'
    >;
    value: unknown;
    values: Record<string, unknown>;
}): string | null => {
    if (typeof field.validate !== 'function') return null;
    if (!field.required && !field.validateWhenEmpty && isFieldValueEmpty(field, value)) {
        return null;
    }

    try {
        const result = field.validate(value, values);
        if (result === true || result === undefined || result === null) {
            return null;
        }
        if (typeof result === 'string') {
            const message = normalizeMessage(result);
            return message || getFieldValidationMessage(field);
        }
        if (result === false) {
            return getFieldValidationMessage(field);
        }
        return null;
    } catch {
        return getFieldValidationMessage(field);
    }
};

export const shouldShowFieldError = ({
    error,
    touched,
    submitAttempted,
}: {
    error?: string | null;
    touched: boolean;
    submitAttempted: boolean;
}): boolean => Boolean(error) && (touched || submitAttempted);

export const resolveFieldValidationState = ({
    field,
    value,
    values,
    error,
    touched,
    dirty,
    submitAttempted,
}: {
    field: Pick<
        ValidationField,
        'required' | 'validate' | 'validateWhenEmpty' | 'validationMessage' | 'type'
    >;
    value: unknown;
    values: Record<string, unknown>;
    error?: string | null;
    touched: boolean;
    dirty: boolean;
    submitAttempted: boolean;
}): FieldValidationState => {
    const showError = shouldShowFieldError({ error, touched, submitAttempted });
    if (showError) return 'invalid';

    if (error) return null;

    if (field.required && isFieldValueEmpty(field, value)) return null;

    const customError = runFieldValidator({ field, value, values });
    if (customError) {
        if (touched || submitAttempted) return 'invalid';
        const hasValue = !isFieldValueEmpty(field, value);
        return hasValue && !dirty ? 'invalid' : null;
    }

    return 'valid';
};
